<template>
  <VcLayout column class="sales-widget pa-6" :class="{ 'sales-widget--loaded': isWidgetReady }">
    <div
      class="sales-widget__title"
      @click="navigate({ path: paymentRoute })"
      @keydown.enter="navigate({ path: paymentRoute })"
      tabindex="0"
      role="button"
      :aria-label="$t('widget.sales_widget.title')"
    >{{ $t('widget.sales_widget.title') }}</div>
    <VcLayout class="sales-widget__wrapper mt-4">
      <VcLayout class="sales-widget__data">
        <PaymentWidgetList
        :isLoading="isWidgetLoading"
        :salesData="salesWidgetData"
        :currency="currency"
        :selected-time-range="selectedTimeRangeValue"
        :time-ranges="timeRanges"
        @navigate="navigate"
        @click-empty-state="goToPaymentWizard"
        @time-range-select="handleTimeRangeChange"/>
      </VcLayout>
      <VcLayout class="sales-widget__forecast">
        <ForecastGraph :isLoading="isForecastLoading" :forecastData="forecastData" :currency="currency"/>
      </VcLayout>
    </VcLayout>
  </VcLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import storageService from '@/modules/common/services/storageService';
import {
  getStartOfWeek,
  getStartOfMonth,
  getStartOfYear,
} from '@/modules/common/utils/dateUtils';
import PaymentWidgetList from './PaymentWidgetList.vue';
import ForecastGraph from './ForecastGraph.vue';

const EMPTY_STATE = 'Empty state';
const OVERDUE_PAYMENTS_VALUES = {
  empty: EMPTY_STATE,
  last_week: '1: 1 - 7 days',
  last_month: '1: 7- 30 days',
  old: '1: 30+ days',
  last_week_and_last_month: '2: 1 - 7 days, 7- 30 days',
  last_week_and_old: '2: 1 - 7 days, 30+ days',
  last_month_and_old: '2: 7- 30 days, 30+ days',
  all: '3: 1 - 7 days, 7- 30 days, 30+ days',
};

export default {
  name: 'SalesWidget',
  components: {
    PaymentWidgetList,
    ForecastGraph,
  },
  data() {
    return {
      selectedTimeRangeValue: null,
      isWidgetLoading: true,
      isForecastLoading: true,
      isWidgetReady: false,
    };
  },
  computed: {
    ...mapGetters('PaymentsStore/SalesWidgetStore', ['salesWidgetData', 'forecastData']),
    ...mapGetters('PaymentSettingsStore', ['currency']),
    isNewDesign() {
      return this.$hasFeature('rollout.sales.sales_widget_adjustments');
    },
    paymentRoute() {
      return '/app/payments/orders';
    },
    timeRanges() {
      const currentUtcTime = new Date();
      return {
        WEEK: {
          startDate: getStartOfWeek(),
          endDate: currentUtcTime,
        },
        MONTH: {
          startDate: getStartOfMonth(),
          endDate: currentUtcTime,
        },
        YEAR: {
          startDate: getStartOfYear(),
          endDate: currentUtcTime,
        },
      };
    },
  },
  async created() {
    this.selectedTimeRangeValue = storageService.get('sales-widget/selected-time-range') || 'MONTH';
    this.addI18nModule('widgets-pov');
    await this.init();
    this.handleMixpanelEvents();
    this.$emit('ready');
    this.isWidgetReady = true;
  },
  methods: {
    ...mapActions('PaymentsStore/SalesWidgetStore', ['fetchSalesWidgetData', 'fetchForecastData']),
    ...mapActions('PaymentSettingsStore', ['fetchSettings']),
    ...mapActions('ModalStore', ['openModal']),
    async init() {
      if (this.isNewDesign) {
        await this.fetchSettings();
        await this.widgetInit(this.timeRanges[this.selectedTimeRangeValue]);
        await this.forecastInit();
      } else {
        await Promise.all([
          this.fetchSettings(),
          this.widgetInit(),
          this.forecastInit(),
        ]);
      }
    },
    async widgetInit(timeRange) {
      await this.fetchSalesWidgetData(timeRange);
      this.isWidgetLoading = false;
    },
    async forecastInit() {
      await this.fetchForecastData();
      this.isForecastLoading = false;
    },
    navigate({ path, queryParams }) {
      this.$emit('sdkAction', 'Navigate', {
        type: 'relative',
        path,
        queryParams,
      });
    },
    goToPaymentWizard() {
      this.$emit('sdkAction', 'OpenModal', {
        modalName: 'PaymentWizard',
        modalProps: [{ source: 'Sales Widget' }],
      });
    },
    handleMixpanelEvents() {
      this.$track('Dashboard Sales Widget components state', {
        overdue_payments_state: this.getOverduePaymentsState(),
        ytd_state: this.getYtdState(),
        pending_estimates: this.getPendingEstimatesState(),
        forecast_state: this.getForcastState(),
      });
    },
    getOverduePaymentsState() {
      const overduePayments = this.salesWidgetData?.overdue_payments_summary?.data;
      if (!overduePayments) {
        return OVERDUE_PAYMENTS_VALUES.empty;
      }
      let key;
      const lastWeek = overduePayments.last_week_count > 0;
      const lastMonth = overduePayments.last_month_count > 0;
      const old = overduePayments.old_count > 0;
      if (lastWeek && lastMonth && old) {
        key = 'all';
      } else {
        key = [
          lastWeek && 'last_week',
          lastMonth && 'last_month',
          old && 'old',
        ].filter(Boolean).join('_and_') || 'empty';
      }
      return OVERDUE_PAYMENTS_VALUES[key];
    },
    getYtdState() {
      const values = {
        empty: EMPTY_STATE,
        ytd_comparison: 'YTD + comparison',
        ytd_no_comparison: 'YTD. No comparison',
      };
      const ytd = this.salesWidgetData?.total_payments?.data;
      if (!ytd) {
        return values.empty;
      }
      let key = 'empty';
      const shouldShowYtdBadge = (new Date()).getMonth() >= 3; // 01-04 or later;
      const currentYearHasPayments = +ytd.current_year !== 0;
      const lastYearHasPayments = +ytd.last_year !== 0;

      if (currentYearHasPayments && lastYearHasPayments && shouldShowYtdBadge) {
        key = 'ytd_comparison';
      } else if (currentYearHasPayments || lastYearHasPayments) {
        key = 'ytd_no_comparison';
      }
      return values[key];
    },
    getPendingEstimatesState() {
      const pendingEstimates = this.salesWidgetData?.pending_estimates?.data;
      if (!pendingEstimates) {
        return EMPTY_STATE;
      }
      return pendingEstimates.count > 0 ? 'Populated' : EMPTY_STATE;
    },
    getForcastState() {
      if (!this.forecastData || Object.keys(this.forecastData).length === 0) {
        return EMPTY_STATE;
      }
      const hasTotalAmount = Object.values(this.forecastData).some((data) => data.total_amount > 0);
      return hasTotalAmount ? 'Populated' : EMPTY_STATE;
    },
    async handleTimeRangeChange(selectedTimeRangeValue) {
      storageService.save('sales-widget/selected-time-range', selectedTimeRangeValue);
      this.selectedTimeRangeValue = selectedTimeRangeValue;
      await this.widgetInit(this.timeRanges[this.selectedTimeRangeValue]);
    },
  },
};
</script>

<style scoped lang="scss">
$xSmall: 505px;
$small: 598px;
$medium: 738px;
$large: 859px;
$xLarge: 1031px;

.sales-widget {
  container-type: inline-size;
  color: var(--gray-darken-5);
  text-align: start;
  width: 100%;
  height: 100%;

  &__title {
    width: fit-content;
    font-size: var(--font-size-medium2);
    font-weight: var(--font-weight-large2);
    cursor: pointer;
    &:hover {
      color: var(--link-color);
    }
  }

  &__wrapper {
    @container (min-width: #{$medium}) {
      gap: 121px;
    }
  }

  &__data {
    flex-grow: 0;
    flex-shrink: 1;
    width: 279px;
  }

  &__forecast {
    display: none;

    @container (min-width: #{$small}) {
      display: flex;
    }
  }
}
</style>
